//auth_state.js
"use strict";

class AuthState extends HttpRequest {
  constructor() {
    super();
    this.fireAuth = null;
    this.currentUser = null;
    this.initPromise = this.#OnAuthStateChanged();

    this.#LogoutHandler();
 
  }

  /**
   * Ensure fireAuth is initialized before usage
   */
  async ready() {
    await this.initPromise;
    if (!this.fireAuth) {
      this.fireAuth = await $Firebase.initAuth();
    }
    return this.fireAuth;
  }

  async #LogoutHandler() {
    document.addEventListener("click", async (event) => {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) {
        return;
      }

      const userLogoutButton = target.closest(".js-logout-user");
      if (userLogoutButton) {
        event.preventDefault();
        await logoutRegularUser(userLogoutButton);
        return;
      }

      const businessLogoutButton = target.closest(".js-logout-business");
      if (businessLogoutButton) {
        event.preventDefault();
        await logoutBusinessUser(businessLogoutButton);
        return;
      }

      const adminLogoutButton = target.closest(".js-logout-admin");
      if (adminLogoutButton) {
        event.preventDefault();
        await logoutSuperAdmin(adminLogoutButton);
      }
    });
  }

  async logoutRegularUser(button = null, options = {}) {
    return this.#LogoutAuthUser({
      button,
      action: "Logout",
      context: "regular-user",
      redirectUrl: options.redirectUrl || button?.dataset?.logoutRedirect || SITE_URL,
      showLoading: options.showLoading ?? true,
    });
  }

  async logoutBusinessUser(button = null, options = {}) {
    return this.#LogoutAuthUser({
      button,
      action: "BusinessLogout",
      context: "business-user",
      redirectUrl: options.redirectUrl || button?.dataset?.logoutRedirect || `${SITE_URL}admin-login/`,
      showLoading: options.showLoading ?? true,
    });
  }

  async logoutSuperAdmin(button = null) {
    const logoutUrl = button?.dataset?.logoutUrl || `${SITE_URL}admin/logout`;
    window.location.assign(logoutUrl);
  }

  async Logout(show_loading = true) {
    return this.logoutRegularUser(null, { showLoading: show_loading });
  }

  async #LogoutAuthUser({ button, action, context, redirectUrl, showLoading }) {
    if (button?.dataset?.logoutBusy === "1") {
      return;
    }

    if (button) {
      button.dataset.logoutBusy = "1";
    }

    if (showLoading) ShowOverlayLoading();
    try {
      try {
        const fireAuth = await this.ready();
        await fireAuth.signOut();
      } catch (error) {
        console.log("firebase logout error:", error);
      }
      await this.#ClearClientAuthState();

      window.location.assign(redirectUrl);
    } catch (error) {
      console.log("error:", error);
    } finally {
      HideOverlayLoading();
      if (button) {
        delete button.dataset.logoutBusy;
      }
    }
  }

  async #ClearClientAuthState() {
    try {
      localStorage.clear();
    } catch (_) {}

    try {
      sessionStorage.clear();
    } catch (_) {}

    this.#ClearReadableAuthCookies();

    try {
      if ("caches" in window) {
        const cacheNames = await caches.keys();
        await Promise.all(cacheNames.map((name) => caches.delete(name)));
      }
    } catch (_) {}

    try {
      if ("indexedDB" in window && typeof indexedDB.databases === "function") {
        const databases = await indexedDB.databases();
        await Promise.all(
          databases
            .map((db) => db && db.name)
            .filter(Boolean)
            .map((name) => new Promise((resolve) => {
              const req = indexedDB.deleteDatabase(name);
              req.onsuccess = req.onerror = req.onblocked = resolve;
            }))
        );
      }
    } catch (_) {}
  }

  #ClearReadableAuthCookies() {
    const cookieNames = document.cookie
      .split(";")
      .map((cookie) => cookie.split("=")[0].trim())
      .filter(Boolean);
    const authCookieNames = new Set(["ut", "login_temp_token", "session"]);
    const host = window.location.hostname;
    const domains = ["", host, "." + host].filter(Boolean);

    cookieNames.forEach((name) => {
      if (!authCookieNames.has(name) && !name.startsWith("firebase") && !name.startsWith("__session")) {
        return;
      }
      domains.forEach((domain) => {
        document.cookie = name + "=; Max-Age=0; Path=/; SameSite=Lax" + (domain ? "; Domain=" + domain : "");
      });
    });
  }

  #AuthStateChanged(config, token) {
    const formData = new FormData();
    formData.append("module", "Authorization");
    formData.append("action", "GetBootstrap");
    formData.append("portal_session", "1");
    for (const key in config) {
      formData.append(key, config[key]);
    }
    return this.PostData(formData, token);
  }

  async #OnAuthStateChanged() {
    this.fireAuth = await $Firebase.initAuth();

    return new Promise((resolve) => {
      // Listen for changes in Firebase Auth state
      this.fireAuth.onAuthStateChanged(async (user) => {
        if (user) {
          try {
            const state = await this.#AuthStateChanged({}, await user.getIdToken());
            if (!state?.data?.user && !state?.user) {
              this.currentUser = null;
              return resolve(null);
            }
            const { uid, email, displayName } = user;
            this.currentUser = { uid, displayName, email };
          } catch (error) {
            // Network/transient failure reaching the auth endpoint must NOT cause
            // a destructive logout. Only an explicit force_logout is terminal.
            const forcedLogout = [
              "APL-AUTH-IDENTITY-INVALID",
              "APL-AUTH-USER-NOT-FOUND",
              "APL-AUTH-USER-INACTIVE",
            ].includes(error?.error_code);
            if (forcedLogout) {
              this.currentUser = null;
              try {
                await this.fireAuth.signOut();
              } catch (_) {}
              return resolve(null);
            }
            const { uid, email, displayName } = user;
            this.currentUser = { uid, displayName, email };
            return resolve(user);
          }
          resolve(user);
        } else {
          this.currentUser = null;
          resolve(null);
        }
      });
    });
  }

  async getCurrentUser() {
    const fireAuth = await this.ready();
    return new Promise((resolve) => {
      const user = fireAuth.currentUser;
      if (user) {
        const { uid, email, displayName } = user;
        resolve({ uid, email, displayName });
      } else {
        fireAuth.onAuthStateChanged((authUser) => {
          if (!authUser) return resolve(null);
          const { uid, email, displayName } = authUser;
          resolve({ uid, email, displayName });
        });
      }
    });
  }
}
const $AuthState = new AuthState();

function logoutRegularUser(button) {
  return $AuthState.logoutRegularUser(button);
}

function logoutBusinessUser(button) {
  return $AuthState.logoutBusinessUser(button);
}

function logoutSuperAdmin(button) {
  return $AuthState.logoutSuperAdmin(button);
}
